// Dish categories - must match backend enum values
export const DISH_CATEGORIES = {
    STARTERS: 'Starters',
    MAINS: 'Mains',
    DESSERTS: 'Desserts',
    BEVERAGES: 'Beverages',
    SALAD: 'Salad',
    SOUP: 'Soup',
    OTHER: 'Other',
};

export const EXPENSE_CATEGORIES = {
    INVENTORY: 'Inventory',
    UTILITIES: 'Utilities',
    LABOR: 'Labor',
    RENT: 'Rent',
    MAINTENANCE: 'Maintenance',
    OTHER: 'Other',
};

export const WEBSOCKET_ENTITIES = {
    ORDER: "Order",
    EXPENSE: "Expense",
}

export const WEBSOCKET_OPERATIONS = {
    CREATE: 'create',
    UPDATE: 'update',
    DELETE: 'delete',
}

export const ORDER_TYPE_VALUES = {
    DINE_IN: 'dine-in',
    TAKEOUT: 'takeout',
    DELIVERY: 'delivery',
}

export const ORDER_TYPES = {
    DINE_IN: 'Dine-in',
    TAKEOUT: 'Takeout',
    DELIVERY: 'Delivery',
}

export const ORDER_TYPE_LABELS = {
    [ORDER_TYPE_VALUES.DINE_IN]: ORDER_TYPES.DINE_IN,
    [ORDER_TYPE_VALUES.TAKEOUT]: ORDER_TYPES.TAKEOUT,
    [ORDER_TYPE_VALUES.DELIVERY]: ORDER_TYPES.DELIVERY,
}

// Item-level kitchen status — must match server ITEM_STATUS
export const ITEM_STATUS = {
    PENDING: 'pending',
    PREPARING: 'preparing',
    READY: 'ready',
    SERVED: 'served',
};

export const ITEM_STATUS_LABELS = {
    [ITEM_STATUS.PENDING]: 'Pending',
    [ITEM_STATUS.PREPARING]: 'Preparing',
    [ITEM_STATUS.READY]: 'Ready',
    [ITEM_STATUS.SERVED]: 'Served',
};

/** Chef kitchen: pending→preparing, preparing→ready only. */
export const CHEF_ITEM_STATUS_NEXT = {
    [ITEM_STATUS.PENDING]: ITEM_STATUS.PREPARING,
    [ITEM_STATUS.PREPARING]: ITEM_STATUS.READY,
    [ITEM_STATUS.READY]: null,
    [ITEM_STATUS.SERVED]: null,
};

/** Next status when staff taps Advance; null means terminal (served). */
export const ITEM_STATUS_NEXT = {
    [ITEM_STATUS.PENDING]: ITEM_STATUS.PREPARING,
    [ITEM_STATUS.PREPARING]: ITEM_STATUS.READY,
    [ITEM_STATUS.READY]: ITEM_STATUS.SERVED,
    [ITEM_STATUS.SERVED]: null,
};

export const ITEM_STATUS_LIST = Object.values(ITEM_STATUS);

/** True when item needs kitchen prep (legacy orders without field default to kitchen). */
export const isKitchenItem = (item) => item?.requiresKitchen !== false;

/** True for OTC / no-kitchen items. */
export const isOtcItem = (item) => item?.requiresKitchen === false;

/** Build order line payload with dish identifiers for server enrichment. */
export const toOrderItemPayload = (item) => {
    const dishId = item.dishId || item._id || item.id;
    const payload = {
        name: item.name,
        quantity: item.quantity,
        price: item.price,
    };
    if (dishId) {
        payload.dishId = dishId;
    }
    if (item.code) {
        payload.code = item.code;
    }
    return payload;
};

// Array of all dish category values for easy iteration
export const DISH_CATEGORY_LIST = Object.values(DISH_CATEGORIES);
export const EXPENSE_CATEGORY_LIST = Object.values(EXPENSE_CATEGORIES);
export const WEBSOCKET_ENTITY_LIST = Object.values(WEBSOCKET_ENTITIES);
export const WEBSOCKET_OPERATION_LIST = Object.values(WEBSOCKET_OPERATIONS);
export const ORDER_TYPE_LIST = Object.values(ORDER_TYPES);
export const ORDER_TYPE_VALUE_LIST = Object.values(ORDER_TYPE_VALUES);

export const GST_REGISTRATION_MODES = {
    UNREGISTERED: 'unregistered',
    COMPOSITION: 'composition',
    REGULAR: 'regular',
};

export const GST_REGISTRATION_MODE_LABELS = {
    [GST_REGISTRATION_MODES.UNREGISTERED]: 'Not GST Registered',
    [GST_REGISTRATION_MODES.COMPOSITION]: 'Composition Scheme',
    [GST_REGISTRATION_MODES.REGULAR]: 'Regular GST',
};

export const GST_ESTABLISHMENT_TYPES = {
    STANDALONE: 'standalone',
    HOTEL_SPECIFIED: 'hotel_specified',
};

export const GST_ESTABLISHMENT_TYPE_LABELS = {
    [GST_ESTABLISHMENT_TYPES.STANDALONE]: 'Standalone Restaurant (5% GST)',
    [GST_ESTABLISHMENT_TYPES.HOTEL_SPECIFIED]: 'Hotel Restaurant — room tariff > ₹7,500 (18% GST)',
};

export const GST_STANDARD_RATES = [5, 18];

export const GST_SAC_DEFAULT = '996331';

export const GST_INVOICE_TYPES = {
    RECEIPT: 'receipt',
    BILL_OF_SUPPLY: 'bill_of_supply',
    TAX_INVOICE: 'tax_invoice',
};

export const GST_INVOICE_TYPE_LABELS = {
    [GST_INVOICE_TYPES.RECEIPT]: 'Invoice',
    [GST_INVOICE_TYPES.BILL_OF_SUPPLY]: 'Bill of Supply',
    [GST_INVOICE_TYPES.TAX_INVOICE]: 'Tax Invoice',
};

export const COMPOSITION_DISCLAIMER =
    'Composition taxable person, not eligible to collect tax on supplies';

export const THERMAL_PAPER_WIDTHS = {
    NARROW: 58,
    WIDE: 80,
};

export const THERMAL_PAPER_WIDTH_LIST = [58, 80];

export const THERMAL_PAPER_WIDTH_LABELS = {
    [THERMAL_PAPER_WIDTHS.NARROW]: '58mm roll (compact)',
    [THERMAL_PAPER_WIDTHS.WIDE]: '80mm roll (standard POS)',
};

/** Printable width inside the roll — less than physical paper width on most printers. */
export const THERMAL_PRINTABLE_WIDTH_MM = {
    [THERMAL_PAPER_WIDTHS.NARROW]: 44,
    [THERMAL_PAPER_WIDTHS.WIDE]: 72,
};

export const getThermalPrintableWidthMm = (paperWidthMm) => (
    THERMAL_PRINTABLE_WIDTH_MM[paperWidthMm] ?? THERMAL_PRINTABLE_WIDTH_MM[THERMAL_PAPER_WIDTHS.NARROW]
);

export const MEDIA_PURPOSES = {
    DISH: 'dish',
    RESTAURANT_COVER: 'restaurant-cover',
    RESTAURANT_LOGO: 'restaurant-logo',
    EXPENSE_RECEIPT: 'expense-receipt',
};

export const RECEIPT_ACCEPT = 'image/jpeg,image/png,image/webp,application/pdf,image/heic,image/heif,.heic,.heif';
export const RECEIPT_MAX_MB = 10;

export const GST_REGISTRATION_MODE_LIST = Object.values(GST_REGISTRATION_MODES);